/**
 * Infinite Scroll Controller for Collection and Search Pages
 *
 * Automatically loads next page of products when user scrolls near bottom.
 * Integrates with existing FilterContainer AJAX system.
 */

class InfiniteScrollController extends HTMLElement {
  constructor() {
    super();

    // State management
    this.currentPage = 1;
    this.totalPages = 1;
    this.loadedPages = new Set([1]);
    this.isLoading = false;
    this.hasMorePages = true;
    this.observer = null;

    // DOM elements
    this.filterContainer = null;
    this.productGrid = null;
    this.paginationElement = null;
    this.loaderElement = null;
    this.endMessageElement = null;

    // Configuration
    this.sectionId = '';
    this.offsetDistance = 500;
  }

  connectedCallback() {
    // Get filter container parent
    this.filterContainer = this.closest('filter-container');

    if (!this.filterContainer) {
      console.warn('InfiniteScrollController: filter-container not found');
      return;
    }

    // Check if AJAX filtering is enabled
    const ajaxEnabled = this.filterContainer.getAttribute('data-ajax-filtering') === 'true';
    if (!ajaxEnabled) {
      console.warn('InfiniteScrollController: AJAX filtering is not enabled');
      return;
    }

    // Check if infinite scroll is enabled
    const infiniteScrollEnabled = this.filterContainer.getAttribute('data-infinite-scroll') === 'true';
    if (!infiniteScrollEnabled) {
      return;
    }

    // Get configuration
    this.sectionId = this.filterContainer.getAttribute('data-filter-section-id') || '';
    this.offsetDistance = parseInt(this.filterContainer.getAttribute('data-infinite-scroll-offset') || '500', 10);

    // Find required DOM elements
    this.productGrid = this.filterContainer.querySelector('.product-grid');

    // Pagination might be inside or outside filter-container
    this.paginationElement = this.filterContainer.querySelector('.pagination');
    if (!this.paginationElement) {
      // Look for pagination outside filter-container (in the next ajax container)
      this.paginationElement = document.querySelector('[data-ajax-container] .pagination');
    }

    if (!this.productGrid || !this.paginationElement) {
      console.warn('InfiniteScrollController: Required elements not found', {
        productGrid: !!this.productGrid,
        paginationElement: !!this.paginationElement
      });
      return;
    }

    // Initialize state from pagination data
    this.initializeState();

    // Create loader and end message elements
    this.createLoaderElement();
    this.createEndMessageElement();

    // Setup IntersectionObserver
    this.setupObserver();

    // Listen for reset events (when filters/sort change)
    this.addEventListener('infinite-scroll:reset', this.handleReset.bind(this));
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initializeState() {
    this.currentPage = parseInt(this.paginationElement.getAttribute('data-current-page') || '1', 10);
    this.totalPages = parseInt(this.paginationElement.getAttribute('data-total-pages') || '1', 10);
    this.loadedPages = new Set([this.currentPage]);
    this.hasMorePages = this.currentPage < this.totalPages;
  }

  createLoaderElement() {
    this.loaderElement = document.createElement('div');
    this.loaderElement.className = 'infinite-scroll-loader';
    this.loaderElement.innerHTML = `
      <div class="infinite-scroll-loader__spinner"></div>
      <p class="infinite-scroll-loader__text">${this.getTranslation('loading')}</p>
    `;
    this.loaderElement.style.display = 'none';

    // Insert loader after product grid
    this.productGrid.parentNode.insertBefore(this.loaderElement, this.productGrid.nextSibling);
  }

  createEndMessageElement() {
    this.endMessageElement = document.createElement('div');
    this.endMessageElement.className = 'infinite-scroll-end';
    this.endMessageElement.innerHTML = `
      <p>${this.getTranslation('end')}</p>
    `;
    this.endMessageElement.style.display = 'none';

    // Insert end message after loader
    this.loaderElement.parentNode.insertBefore(this.endMessageElement, this.loaderElement.nextSibling);
  }

  setupObserver() {
    const options = {
      root: null,
      rootMargin: `0px 0px ${this.offsetDistance}px 0px`,
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMorePages && !this.isLoading) {
          this.loadNextPage();
        }
      });
    }, options);

    // Observe the loader element
    if (this.hasMorePages) {
      this.loaderElement.style.display = 'block';
      this.observer.observe(this.loaderElement);
    }
  }

  async loadNextPage() {
    if (this.isLoading || !this.hasMorePages) {
      return;
    }

    const nextPageUrl = this.paginationElement.getAttribute('data-next-url');
    if (!nextPageUrl) {
      this.hasMorePages = false;
      this.showEndMessage();
      return;
    }

    this.isLoading = true;
    this.showLoader();

    try {
      // Load full page (not section_id) to get both products and pagination
      const url = new URL(nextPageUrl, window.location.origin);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Parse response and get updated pagination info
      const paginationInfo = this.parseAndAppendProducts(html);

      // Update current page
      this.currentPage++;
      this.loadedPages.add(this.currentPage);

      // Update total pages from the parsed pagination
      if (paginationInfo && paginationInfo.totalPages) {
        this.totalPages = paginationInfo.totalPages;
      }

      // Update browser URL
      this.updateBrowserUrl(nextPageUrl);

      // Dispatch event for other components to reinitialize
      this.dispatchEvent(new CustomEvent('infinite-scroll:products-loaded', {
        bubbles: true,
        detail: { page: this.currentPage }
      }));

      // Check if we've reached the end
      const hasNextUrl = this.paginationElement.getAttribute('data-next-url');
      if (this.currentPage >= this.totalPages || !hasNextUrl) {
        this.hasMorePages = false;
        this.observer.disconnect();
        this.showEndMessage();
      }

    } catch (error) {
      console.error('InfiniteScrollController: Error loading next page', error);
      this.showError();
    } finally {
      this.isLoading = false;
      // Don't hide the loader - keep it visible so the observer can trigger again
      // The loader will be hidden only when we reach the end (showEndMessage)
    }
  }

  parseAndAppendProducts(html) {
    const template = document.createElement('template');
    template.innerHTML = html;

    // Extract new products from the response
    const newProductGrid = template.content.querySelector('.product-grid');
    if (newProductGrid) {
      // Append new products to existing grid
      const newProducts = Array.from(newProductGrid.children);
      newProducts.forEach(product => {
        this.productGrid.appendChild(product);
      });
    }

    // Update pagination data attributes and return info
    const newPagination = template.content.querySelector('.pagination');
    let paginationInfo = null;

    if (newPagination) {
      const newNextUrl = newPagination.getAttribute('data-next-url');
      const newCurrentPage = newPagination.getAttribute('data-current-page');
      const newTotalPages = newPagination.getAttribute('data-total-pages');

      if (newNextUrl) {
        this.paginationElement.setAttribute('data-next-url', newNextUrl);
      } else {
        this.paginationElement.removeAttribute('data-next-url');
      }

      if (newCurrentPage) {
        this.paginationElement.setAttribute('data-current-page', newCurrentPage);
      }

      if (newTotalPages) {
        this.paginationElement.setAttribute('data-total-pages', newTotalPages);
      }

      // Return pagination info
      paginationInfo = {
        nextUrl: newNextUrl || null,
        currentPage: parseInt(newCurrentPage, 10) || null,
        totalPages: parseInt(newTotalPages, 10) || null
      };
    }

    return paginationInfo;
  }

  updateBrowserUrl(url) {
    // Use replaceState instead of pushState to avoid polluting browser history
    // This way, pressing back button returns to the previous page (e.g., homepage)
    // instead of going through all loaded pages (page=5 → page=4 → page=3...)
    if (window.history && window.history.replaceState) {
      window.history.replaceState({ page: this.currentPage }, '', url);
    }
  }

  handleReset() {
    // Reset state when filters or sorting change
    this.currentPage = 1;
    this.loadedPages = new Set([1]);
    this.isLoading = false;

    // Re-find all DOM elements after AJAX update (they have been replaced)
    this.productGrid = this.filterContainer.querySelector('.product-grid');

    this.paginationElement = this.filterContainer.querySelector('.pagination');
    if (!this.paginationElement) {
      this.paginationElement = document.querySelector('[data-ajax-container] .pagination');
    }

    if (!this.productGrid) {
      console.warn('InfiniteScrollController: Product grid not found after reset');
      return;
    }

    if (!this.paginationElement) {
      console.warn('InfiniteScrollController: Pagination not found after reset');
      this.hasMorePages = false;
      return;
    }

    // Remove old loader and end message elements if they exist
    if (this.loaderElement && this.loaderElement.parentNode) {
      this.loaderElement.parentNode.removeChild(this.loaderElement);
    }
    if (this.endMessageElement && this.endMessageElement.parentNode) {
      this.endMessageElement.parentNode.removeChild(this.endMessageElement);
    }

    // Re-create loader and end message elements in the new DOM
    this.createLoaderElement();
    this.createEndMessageElement();

    // Re-initialize state from updated pagination
    this.initializeState();

    // Reconnect observer if there are more pages
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.hasMorePages) {
      this.setupObserver();
    } else {
      this.hideLoader();
    }
  }

  showLoader() {
    if (this.loaderElement) {
      this.loaderElement.style.display = 'block';
      this.loaderElement.classList.add('infinite-scroll-loader--loading');
      this.loaderElement.classList.remove('infinite-scroll-loader--error');
    }
  }

  hideLoader() {
    if (this.loaderElement) {
      this.loaderElement.classList.remove('infinite-scroll-loader--loading');
      // Keep element visible but without spinner for IntersectionObserver
    }
  }

  showEndMessage() {
    this.hideLoader();
    if (this.endMessageElement) {
      this.endMessageElement.style.display = 'block';
    }
  }

  hideEndMessage() {
    if (this.endMessageElement) {
      this.endMessageElement.style.display = 'none';
    }
  }

  showError() {
    if (this.loaderElement) {
      this.loaderElement.classList.add('infinite-scroll-loader--error');
      const textElement = this.loaderElement.querySelector('.infinite-scroll-loader__text');
      if (textElement) {
        textElement.textContent = this.getTranslation('error');
      }
    }
  }

  hideError() {
    if (this.loaderElement) {
      this.loaderElement.classList.remove('infinite-scroll-loader--error');
      const textElement = this.loaderElement.querySelector('.infinite-scroll-loader__text');
      if (textElement) {
        textElement.textContent = this.getTranslation('loading');
      }
    }
  }

  getTranslation(key) {
    const translations = {
      loading: 'general.pagination.infinite_scroll_loading',
      end: 'general.pagination.infinite_scroll_end',
      error: 'general.pagination.infinite_scroll_error'
    };

    const translationKey = translations[key];
    if (!translationKey) {
      return '';
    }

    // Try to get translation from theme translations
    if (window.theme && window.theme.strings && window.theme.strings[translationKey]) {
      return window.theme.strings[translationKey];
    }

    // Fallback to data attribute or default
    const fallbacks = {
      loading: 'Loading more products...',
      end: 'No more products',
      error: 'Error loading products. Please try again.'
    };

    return fallbacks[key] || '';
  }
}

// Register custom element
customElements.define('infinite-scroll-controller', InfiniteScrollController);
